import { Component, EventEmitter, Input, Output, ViewChild, NgZone, OnChanges, OnDestroy, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { filter, Subscription } from 'rxjs';
import { DeadLetterListItem } from '../../models/dead-letter-list-item.model';

export interface SelectionChangeEvent {
  id: string;
  selected: boolean;
}

@Component({
    selector: 'app-dead-letter-list',
    templateUrl: './dead-letter-list.component.html',
    styleUrls: ['./dead-letter-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DeadLetterListComponent implements OnChanges, OnDestroy {
  private viewportInstance?: CdkVirtualScrollViewport;
  private viewportSubscription?: Subscription;

  @ViewChild(CdkVirtualScrollViewport)
  set viewport(value: CdkVirtualScrollViewport | undefined) {
    if (this.viewportInstance === value) { return; }

    this.viewportSubscription?.unsubscribe();
    this.viewportInstance = value;

    if (!value) { return; }

    this.viewportSubscription = value.renderedRangeStream.pipe(
      filter(() => !this.appendLoading && this.items.length > 0),
      filter(range => range.end >= this.items.length - 5)
    ).subscribe(() => {
      this.ngZone.run(() => this.loadMore.emit());
    });

    queueMicrotask(() => {
      if (this.viewportInstance === value) {
        this.checkShouldLoadMore();
      }
    });
  }

  constructor(private readonly ngZone: NgZone) {}

  @Input() items: DeadLetterListItem[] = [];
  @Input() selectedIds: Set<string> = new Set();
  @Input() selectAll = false;
  @Input() initialLoading = false;
  @Input() appendLoading = false;
  @Input() initialError: string | null = null;
  @Input() hasActiveFilters = false;
  @Input() actionInProgressIds: Set<string> = new Set();

  @Output() loadMore = new EventEmitter<void>();
  @Output() selectionChange = new EventEmitter<SelectionChangeEvent>();
  @Output() selectAllChange = new EventEmitter<boolean>();
  @Output() idClick = new EventEmitter<string>();
  @Output() removeClick = new EventEmitter<string>();
  @Output() replayClick = new EventEmitter<string>();
  @Output() retryFirstPage = new EventEmitter<void>();

  isSelected(id: string): boolean {
    return this.selectAll || this.selectedIds.has(id);
  }

  onSelectAllChange(checked: boolean): void {
    this.selectAllChange.emit(checked);
  }

  onSelectionChange(id: string, checked: boolean): void {
    this.selectionChange.emit({ id, selected: checked });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['items'] || changes['appendLoading']) {
      this.checkShouldLoadMore();
    }
  }

  ngOnDestroy(): void {
    this.viewportSubscription?.unsubscribe();
  }

  private checkShouldLoadMore(): void {
    if (!this.viewportInstance || this.appendLoading || this.items.length === 0) { return; }
    const range = this.viewportInstance.getRenderedRange();
    if (range.end >= this.items.length - 5) {
      this.loadMore.emit();
    }
  }

  onIdClick(id: string): void {
    this.idClick.emit(id);
  }

  onRemoveClick(id: string): void {
    this.removeClick.emit(id);
  }

  onReplayClick(id: string): void {
    this.replayClick.emit(id);
  }

  onRetryClick(): void {
    this.retryFirstPage.emit();
  }

  scrollToTop(): void {
    if (this.viewportInstance) {
      this.viewportInstance.scrollToIndex(0);
    }
  }
}
